import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { ArrowLeft, CheckCircle, CreditCard, Loader2, Shield, Zap } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function Subscription() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processingPayment, setProcessingPayment] = useState(null);

  useEffect(() => {
    // Check for payment status from URL
    const paymentStatus = searchParams.get('payment');
    if (paymentStatus === 'success') {
      toast.success('Payment successful! Your subscription is now active.');
      // Clear the URL params
      navigate('/subscription', { replace: true });
    } else if (paymentStatus === 'cancelled') {
      toast.error('Payment was cancelled.');
      navigate('/subscription', { replace: true });
    }
    
    fetchSubscription();
  }, []);

  const fetchSubscription = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/subscription/status`);
      setSubscription(response.data);
    } catch (error) {
      toast.error('Failed to load subscription');
    } finally {
      setLoading(false);
    }
  };

  const handleSubscribe = async (tier) => {
    setProcessingPayment(tier);
    try {
      // Initiate PayFast payment
      const response = await axios.post(`${API_URL}/api/payfast/initiate?tier=${tier}`);
      const { payment_url, form_data } = response.data;
      
      // Create and submit form to PayFast
      const form = document.createElement('form');
      form.method = 'POST';
      form.action = payment_url;
      
      Object.entries(form_data).forEach(([key, value]) => {
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = key;
        input.value = value;
        form.appendChild(input);
      });
      
      document.body.appendChild(form);
      form.submit();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to initiate payment');
      setProcessingPayment(null);
    }
  };

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-sky-50">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600"></div>
    </div>
  );

  const isTrial = subscription?.status === 'trial';
  const isActive = subscription?.status === 'active';
  const isBalfour = subscription?.subscription_tier === 'trial_balfour';
  const monthlyLimit = subscription?.monthly_limit || 8;
  const currentUsage = subscription?.current_month_usage || 0;
  const currentTier = subscription?.subscription_tier;

  return (
    <div className="min-h-screen bg-sky-50" data-testid="subscription-page">
      <header className="glass-header py-4 px-6">
        <div className="max-w-4xl mx-auto">
          <Button onClick={() => navigate('/dashboard')} variant="ghost" data-testid="back-to-dashboard">
            <ArrowLeft className="w-4 h-4 mr-2" /> Back to Dashboard
          </Button>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-12">
        <div className="glass-card p-8">
          <h2 className="text-3xl font-bold text-slate-900 mb-6">Subscription</h2>

          {/* Current Status Section */}
          <div className="bg-white/50 border border-slate-200 rounded-2xl p-6 mb-8">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-semibold text-slate-900">Current Status</h3>
              {isTrial ? (
                <span className="flex items-center px-4 py-2 bg-blue-100 text-blue-700 rounded-full font-medium" data-testid="subscription-trial">
                  <CheckCircle className="w-4 h-4 mr-2" /> Trial {isBalfour ? '(Balfour Primary)' : ''}
                </span>
              ) : isActive ? (
                <span className="flex items-center px-4 py-2 bg-green-100 text-green-700 rounded-full font-medium" data-testid="subscription-active">
                  <CheckCircle className="w-4 h-4 mr-2" /> {currentTier === 'r25_monthly' ? 'Basic Plan' : 'Pro Plan'}
                </span>
              ) : (
                <span className="flex items-center px-4 py-2 bg-slate-100 text-slate-700 rounded-full font-medium">
                  Inactive
                </span>
              )}
            </div>
            <div className="space-y-2">
              <p className="text-slate-700">
                <span className="font-semibold">Monthly Limit:</span> {monthlyLimit} lesson plans
              </p>
              <p className="text-slate-700">
                <span className="font-semibold">Used This Month:</span> {currentUsage} of {monthlyLimit}
              </p>
              <div className="mt-3">
                <div className="w-full bg-slate-200 rounded-full h-3">
                  <div 
                    className="bg-teal-600 h-3 rounded-full transition-all duration-300"
                    style={{ width: `${Math.min((currentUsage / monthlyLimit) * 100, 100)}%` }}
                  ></div>
                </div>
              </div>
              <p className="text-slate-600 text-sm mt-2">
                {isTrial
                  ? `You are on the ${isBalfour ? 'Balfour Primary trial (20 free plans/month)' : 'standard trial (8 free plans/month)'}. Subscribe below for more!`
                  : isActive
                  ? 'You have full access to all features.'
                  : 'Subscribe to access premium features.'}
              </p>
            </div>
          </div>

          {/* Subscription Plans */}
          <div className="bg-gradient-to-br from-teal-50 to-sky-50 border border-teal-200 rounded-2xl p-8">
            <div className="text-center mb-8">
              <CreditCard className="w-12 h-12 text-teal-600 mx-auto mb-4" />
              <h3 className="text-2xl font-bold text-slate-900 mb-2">
                {isTrial ? 'Upgrade Your Account' : 'Change Your Plan'}
              </h3>
              <p className="text-slate-600">
                Pay securely with PayFast - supports all South African banks
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              {/* R25 Plan */}
              <div className={`bg-white rounded-xl p-6 border-2 transition-colors ${
                currentTier === 'r25_monthly' ? 'border-green-500 bg-green-50' : 'border-slate-200 hover:border-teal-500'
              }`}>
                {currentTier === 'r25_monthly' && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-green-600 text-white px-4 py-1 rounded-full text-sm font-medium">
                    Current Plan
                  </div>
                )}
                <div className="text-center mb-4">
                  <div className="text-3xl font-bold text-teal-600 mb-1">R25<span className="text-lg text-slate-600">/month</span></div>
                  <p className="text-slate-700 font-medium">Basic Plan</p>
                </div>
                <ul className="space-y-3 mb-6 text-slate-700">
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-teal-600 mr-2 flex-shrink-0 mt-0.5" />
                    <span><strong>20</strong> lesson plans per month</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-teal-600 mr-2 flex-shrink-0 mt-0.5" />
                    <span>AI-powered CAPS-aligned content</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-teal-600 mr-2 flex-shrink-0 mt-0.5" />
                    <span>PDF/Word downloads</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-teal-600 mr-2 flex-shrink-0 mt-0.5" />
                    <span>Unlimited quizzes & worksheets</span>
                  </li>
                </ul>
                <Button
                  onClick={() => handleSubscribe('r25_monthly')}
                  disabled={processingPayment !== null || currentTier === 'r25_monthly'}
                  className="w-full bg-teal-600 hover:bg-teal-700 text-white rounded-full py-3"
                  data-testid="subscribe-r25-button"
                >
                  {processingPayment === 'r25_monthly' ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Processing...
                    </>
                  ) : currentTier === 'r25_monthly' ? (
                    'Current Plan'
                  ) : (
                    <>
                      <Zap className="w-4 h-4 mr-2" /> Subscribe - R25/month
                    </>
                  )}
                </Button>
              </div>

              {/* R50 Plan */}
              <div className={`bg-white rounded-xl p-6 border-2 relative ${
                currentTier === 'r50_monthly' ? 'border-green-500 bg-green-50' : 'border-teal-500'
              }`}>
                {currentTier !== 'r50_monthly' && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-teal-600 text-white px-4 py-1 rounded-full text-sm font-medium">
                    Popular
                  </div>
                )}
                {currentTier === 'r50_monthly' && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-green-600 text-white px-4 py-1 rounded-full text-sm font-medium">
                    Current Plan
                  </div>
                )}
                <div className="text-center mb-4">
                  <div className="text-3xl font-bold text-teal-600 mb-1">R50<span className="text-lg text-slate-600">/month</span></div>
                  <p className="text-slate-700 font-medium">Pro Plan</p>
                </div>
                <ul className="space-y-3 mb-6 text-slate-700">
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-teal-600 mr-2 flex-shrink-0 mt-0.5" />
                    <span><strong>40</strong> lesson plans per month</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-teal-600 mr-2 flex-shrink-0 mt-0.5" />
                    <span>AI-powered CAPS-aligned content</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-teal-600 mr-2 flex-shrink-0 mt-0.5" />
                    <span>PDF/Word downloads</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-teal-600 mr-2 flex-shrink-0 mt-0.5" />
                    <span>Unlimited quizzes & worksheets</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-teal-600 mr-2 flex-shrink-0 mt-0.5" />
                    <span>Priority support</span>
                  </li>
                </ul>
                <Button
                  onClick={() => handleSubscribe('r50_monthly')}
                  disabled={processingPayment !== null || currentTier === 'r50_monthly'}
                  className="w-full bg-teal-600 hover:bg-teal-700 text-white rounded-full py-3"
                  data-testid="subscribe-r50-button"
                >
                  {processingPayment === 'r50_monthly' ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Processing...
                    </>
                  ) : currentTier === 'r50_monthly' ? (
                    'Current Plan'
                  ) : (
                    <>
                      <Zap className="w-4 h-4 mr-2" /> Subscribe - R50/month
                    </>
                  )}
                </Button>
              </div>
            </div>

            {/* Security Notice */}
            <div className="flex items-center justify-center gap-2 text-sm text-slate-600 mt-6">
              <Shield className="w-4 h-4" />
              <span>Secure payment powered by PayFast</span>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
